import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip,
  BarChart, Bar,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Wallet, FileBarChart, Building2 } from 'lucide-react';

const todayStr = () => new Date().toISOString().split('T')[0];
const isoDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; };
const isoMonthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };
const isoYearStart = () => { const d = new Date(); return `${d.getFullYear()}-01-01`; };

const fmtEuro = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDateShort = (d) => { try { return new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }); } catch { return d; } };

const PRESETS = [
  { key: 'oggi', label: 'Oggi', da: todayStr, a: todayStr },
  { key: 'settimana', label: '7 giorni', da: () => isoDaysAgo(6), a: todayStr },
  { key: 'mese', label: 'Questo mese', da: isoMonthStart, a: todayStr },
  { key: 'anno', label: "Quest'anno", da: isoYearStart, a: todayStr },
];

const TrendBadge = ({ pct, direzione, size = 'normal' }) => {
  const style = direzione === 'positivo'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : direzione === 'negativo'
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-slate-100 text-slate-500 border-slate-200';
  const Icon = direzione === 'positivo' ? TrendingUp : direzione === 'negativo' ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${style} ${size === 'small' ? 'text-[10px]' : 'text-xs'}`}>
      <Icon size={size === 'small' ? 10 : 12} />
      {pct > 0 ? '+' : ''}{pct}%
    </span>
  );
};

export const AgenziaReport = () => {
  const [preset, setPreset] = useState('mese');
  const [da, setDa] = useState(isoMonthStart());
  const [a, setA] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/agenzia/report?da=${da}&a=${a}`);
      setReport(res.data);
    } catch {
      toast.error('Errore nel caricamento del report');
    } finally {
      setLoading(false);
    }
  }, [da, a]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const applyPreset = (p) => {
    setPreset(p.key);
    setDa(p.da());
    setA(p.a());
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Report Incassi</h1>
          <p className="text-sm text-slate-500">Andamento e confronto per compagnia - CRM Agenzia</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map(p => (
          <Button key={p.key} size="sm" variant={preset === p.key ? 'default' : 'outline'} onClick={() => applyPreset(p)}>
            {p.label}
          </Button>
        ))}
        <div className="flex items-center gap-2 ml-2">
          <Label className="text-xs text-slate-500">Da</Label>
          <Input type="date" className="w-36 h-8 text-sm" value={da} onChange={(e) => { setPreset(null); setDa(e.target.value); }} />
          <Label className="text-xs text-slate-500">A</Label>
          <Input type="date" className="w-36 h-8 text-sm" value={a} onChange={(e) => { setPreset(null); setA(e.target.value); }} />
        </div>
      </div>

      {loading || !report ? (
        <div className="text-center py-12 text-slate-400">Caricamento...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide mb-1">
                <Wallet size={14} /> Totale incassato
              </div>
              <p className="text-2xl font-bold text-slate-900">{fmtEuro(report.totale)}</p>
              <div className="mt-2 flex items-center gap-2">
                <TrendBadge pct={report.trend_pct} direzione={report.trend_direzione} />
                <span className="text-[11px] text-slate-400">vs periodo precedente ({fmtEuro(report.periodo_precedente.totale)})</span>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide mb-1">
                <FileBarChart size={14} /> Polizze incassate
              </div>
              <p className="text-2xl font-bold text-slate-900">{report.numero_polizze}</p>
              <p className="text-[11px] text-slate-400 mt-2">dal {fmtDateShort(report.da)} al {fmtDateShort(report.a)}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide mb-1">
                <Building2 size={14} /> Compagnie attive
              </div>
              <p className="text-2xl font-bold text-slate-900">{report.per_compagnia.length}</p>
              <p className="text-[11px] text-slate-400 mt-2">con almeno un incasso nel periodo</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm font-semibold text-slate-800 mb-3">Andamento giornaliero</p>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={report.andamento_giornaliero}>
                <defs>
                  <linearGradient id="agenziaTotaleFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="data" tickFormatter={fmtDateShort} tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} width={70} tickFormatter={(v) => `€${v}`} />
                <Tooltip
                  formatter={(v) => [fmtEuro(v), 'Incassato']}
                  labelFormatter={fmtDateShort}
                  contentStyle={{ border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }}
                />
                <Area type="monotone" dataKey="totale" stroke="#2563EB" strokeWidth={2} fill="url(#agenziaTotaleFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200">
              <p className="text-sm font-semibold text-slate-800">Per compagnia</p>
            </div>
            {report.per_compagnia.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm">Nessun incasso nel periodo selezionato</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-0">
                <div style={{ height: Math.max(200, report.per_compagnia.length * 40) }} className="p-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={report.per_compagnia} layout="vertical" margin={{ left: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                      <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                      <YAxis type="category" dataKey="compagnia" width={110} tick={{ fill: '#334155', fontSize: 12 }} />
                      <Tooltip formatter={(v) => [fmtEuro(v), 'Totale']} contentStyle={{ border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="totale" fill="#2563EB" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="divide-y divide-slate-100 border-t md:border-t-0 md:border-l border-slate-100">
                  {report.per_compagnia.map((c) => (
                    <div key={c.compagnia} className="flex items-center justify-between px-4 py-2.5">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{c.compagnia}</p>
                        <p className="text-[11px] text-slate-400">{c.numero_polizze} polizze</p>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1">
                        <span className="text-sm font-semibold text-slate-900">{fmtEuro(c.totale)}</span>
                        <TrendBadge pct={c.trend_pct} direzione={c.trend_direzione} size="small" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
